import { Base } from '@studiometa/js-toolkit';

/**
 * @typedef {object} MenuItemRefs
 * @property {HTMLAnchorElement} link[]
 */

/**
 * @typedef {object} MenuItemInterface
 * @property {MenuItemRefs} $refs
 */

/**
 * MenuItem component
 */
export default class MenuItem extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'MenuItem',
    components: {
      MenuItem,
    },
    refs: ['link[]'],
    emits: ['click', 'media-update'],
    options: {
      img: String,
      video: String,
    },
    log: true,
  };

  /**
   * Should component react on click
   * @returns {boolean}
   */
  get shouldReactOnClick() {
    return (
      'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0
    );
  }

  /**
   * Mounted hook
   * @this {MenuItem & MenuItemInterface}
   */
  mounted() {
    // Add accessibility
    if (this.$refs.link) {
      this.$refs.link.forEach((link) => {
        link.setAttribute('aria-controls', this.$id);
      });
    }
  }

  /**
   * Mouseenter
   */
  onMouseenter() {
    if (this.shouldReactOnClick) {
      return;
    }

    this.updateMedias();
    this.open();
  }

  /**
   * Mouseleave
   */
  onMouseleave() {
    if (this.shouldReactOnClick) {
      return;
    }

    this.close();
  }

  /**
   * On $refs.link click
   * event object
   * @this {MenuItem & MenuItemInterface}
   */
  onLinkClick() {
    if (!this.shouldReactOnClick) {
      return;
    }

    this.$emit('click');
    this.updateMedias();
  }

  /**
   * onBackClose
   * @this {MenuItem & MenuItemInterface}
   */
  onBackClick() {
    this.close();
  }

  /**
   * Open
   * @this {MenuItem & MenuItemInterface}
   */
  async open() {
    if (this.isOpen) {
      return;
    }

    this.isOpen = true;
  }

  /**
   * Close
   * @this {MenuItem & MenuItemInterface}
   */
  close() {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
  }

  /**
   * Update medias.
   */
  updateMedias() {
    if (this.$options.video !== '') {
      this.$emit('media-update', this.$options.video, 'video');
    }

    if (this.$options.img !== '') {
      this.$emit('media-update', this.$options.img, 'img');
    }
  }

  /**
   * Keyboard nav
   * @param {object} props keyed service props.
   * @param {boolean} props.TAB TAB
   * @param {boolean} props.ESC ESC
   * @param {boolean} props.ENTER ENTER
   */
  keyed({ TAB, ESC, ENTER }) {
    const hasFocusElementWithin = this.$el.contains(document.activeElement);

    if (ESC) {
      this.close();
      if (hasFocusElementWithin) {
        document.activeElement.blur();
      }
    }

    if (this.shouldReactOnClick) {
      if (ENTER && hasFocusElementWithin) {
        this.open();
      } else if (ENTER && !hasFocusElementWithin) {
        this.close();
      }
    } else if (TAB && hasFocusElementWithin) {
      this.open();
    } else if (TAB && !hasFocusElementWithin) {
      this.close();
    }
  }
}
